import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppKit, useAppKitAccount } from "@reown/appkit/react";

const Home = () => {
  const navigate = useNavigate();
  const { open } = useAppKit();
  const { isConnected } = useAppKitAccount();

  return (
    <div>
      {/* HERO */}
      <section className="px-6 md:px-12 py-20 text-center border-b border-gray-800">
        <h1 className="text-4xl md:text-6xl font-bold text-blue-400 mb-6">
        Kind Reward Network – A Smart Earning Game on Blockchain

        </h1>

        <p className="text-lg md:text-xl max-w-3xl mx-auto text-white">
          Kind Reward Network is a decentralized earning system designed like a growth game, where users earn from real activity inside the network. <br></br>
        </p>

        <button
          onClick={() => {
            if (isConnected) navigate("/dashboard");
            else open("Connect");
          }}
          className="mt-10 px-10 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl font-semibold text-white"
        >
          {isConnected ? "Go to Dashboard" : "Connect Wallet"}
        </button>
      </section>

      {/* ABOUT */}
      <section className="px-6 md:px-12 py-16 max-w-5xl mx-auto">
        <div className="bg-white/5 border border-gray-800 p-6 rounded-2xl">
          <h2 className="text-2xl font-bold text-blue-400 mb-4">
            What is Kind Reward Network?
          </h2>
          <p className="text-white leading-7">
            Kind Reward Network is a decentralized earning system designed like a growth game, where users earn from real activity inside the network. Once you join, you activate a package and become part of a system that continuously distributes rewards based on participation and growth.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 md:px-12 py-16">
        <h2 className="text-2xl font-bold text-blue-400 mb-10 text-center">
          🎮 How It Works
        </h2>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <div className="bg-white/5 border border-blue-500/30 p-6 rounded-2xl text-white">
            When you buy a package, a one‑time{" "}
            <span className="text-yellow-400 font-semibold">
              45% system contribution (tax)
            </span>{" "}
            is applied.
          </div>

          <div className="bg-white/5 border border-purple-500/30 p-6 rounded-2xl">
            <ul className="list-disc ml-5 space-y-2 text-white">
              <li className="text-white">Funds the reward pool</li>
              <li className="text-white">Pays referral bonuses (6 levels)</li>
              <li className="text-white">Supports marketing & growth</li>
              <li className="text-white">Enables ongoing airdrops</li>
            </ul>
          </div>
        </div>

        <p className="text-center mt-6 text-white">
          The remaining part of your package stays tied to your position in the system.
        </p>
      </section>

      {/* EARNINGS */}
      <section className="px-6 md:px-12 py-16 border-y border-gray-800">
        <h2 className="text-2xl font-bold text-blue-400 mb-10 text-center">
          💰 How You Earn
        </h2>

        <p className="text-center text-white mb-6">
          You earn from 3 main streams:
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="bg-white/5 border border-gray-700 p-6 rounded-2xl text-center">
            <h3 className="font-semibold text-lg mb-2 text-blue-400">
              Daily Reward Pool
            </h3>
            <p className="text-white">Grows as more users join</p>
          </div>

          <div className="bg-white/5 border border-gray-700 p-6 rounded-2xl text-center">
            <h3 className="font-semibold text-lg mb-2 text-purple-400">
              Airdrops
            </h3>
            <p className="text-white">Bonus tokens distributed over time</p>
          </div>

          <div className="bg-white/5 border border-gray-700 p-6 rounded-2xl text-center">
            <h3 className="font-semibold text-lg mb-2 text-green-400">
              6-Level Referrals
            </h3>
            <p className="text-white">Earn from your network</p>
          </div>
        </div>
      </section>

      {/* JOIN */}
      <section className="px-6 md:px-12 py-16">
        <h2 className="text-2xl font-bold text-blue-400 mb-10 text-center">
          🧾 How to Join
        </h2>

        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "1. Choose a package",
            "2. Connect your wallet",
            "3. Buy (activation)",
            "4. Start earning immediately",
          ].map((step, i) => (
            <div
              key={i}
              className="bg-white/5 border border-blue-500/20 p-6 rounded-2xl"
            >
              <p className="text-white">{step.replace(/^\d+\.\s*/, "")}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="px-6 md:px-12 py-16 border-y border-gray-800 text-center">
        <h2 className="text-2xl font-bold text-blue-400 mb-6">
          🔥 Why Join Early
        </h2>

        <ul className="text-white space-y-3 max-w-2xl mx-auto leading-relaxed">
          <li className="text-white">Early users benefit more</li>
          <li className="text-white">Rewards increase as system grows</li>
          <li className="text-white">Fully automated smart contract</li>
          <li className="text-white">No manual control</li>
        </ul>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-20 text-center">
        <h2 className="text-3xl font-bold mb-4 text-blue-400">
          👉 Join early. Grow with the system. Earn as the network expands.
        </h2>

        <button
          onClick={() => {
            navigate("/packages");
            window.scrollTo(0, 0);
          }}
          className="mt-6 px-10 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl font-semibold text-white"
        >
          Start Now
        </button>

        <p className="text-white text-sm mt-6 max-w-2xl mx-auto">
          ⚠️ Earnings are not guaranteed and depend on real usage and participation.
        </p>
      </section>
    </div>
  );
};

export default Home;